import type { AuthSessionResponse, RequestOptions } from "./types";
import { BROWSER_STORAGE_KEYS, clearCoachLastConversationId } from "../browser-storage";
import { currentBrowserPushSubscription, unsubscribeBrowserPush } from "../components/browser-notifications";
import { resetAnalytics } from "../analytics";
import { setRequestIdTag } from "../observability/sentry";

const REQUEST_ID_HEADER = "X-Disciplio-Request-Id";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080";
const ACCESS_TOKEN_KEY = "disciplio-api-access-token-v2";
const REFRESH_TOKEN_KEY = "disciplio-api-refresh-token-v2";
const LEGACY_TOKEN_KEYS = ["disciplio-access-token-v1", "disciplio-refresh-token-v1"];
const USER_STATE_KEYS = [BROWSER_STORAGE_KEYS.workspace];

let refreshInFlight: Promise<boolean> | null = null;
let accessToken: string | null = null;
let lastRefreshError: DisciplioApiError | null = null;
let refreshChannel: BroadcastChannel | null = null;
let refreshChannelInitialized = false;
let latestRefreshOutcome: { issuedAt: number; outcome: RefreshOutcome } | null = null;
const ACCESS_TOKEN_REFRESH_SKEW_MS = 30_000;
const REFRESH_LOCK_NAME = "disciplio-refresh-access-token";
const REFRESH_CHANNEL_NAME = "disciplio-auth-refresh";

type RefreshFailure = {
  status: number;
  message?: string;
  body: Record<string, unknown> | null;
  retryAfter: number | null;
};

type RefreshOutcome = {
  ok: boolean;
  terminal: boolean;
  accessToken?: string;
  failure?: RefreshFailure;
};

LEGACY_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));

export class DisciplioApiError extends Error {
  readonly status: number;
  readonly body: Record<string, unknown> | null;
  readonly retryAfter: number | null;

  constructor(
    status: number,
    message?: string,
    body: Record<string, unknown> | null = null,
    retryAfter?: number | null,
  ) {
    super(message ?? `Disciplio API ${status}`);
    this.name = "DisciplioApiError";
    this.status = status;
    this.body = body;
    this.retryAfter = parseRetryAfter(retryAfter) ?? parseRetryAfter(body?.retryAfter);
  }

  get retryAfterSeconds() {
    return this.retryAfter;
  }

  get quota() {
    if (this.status !== 429 || !this.body || this.body.type !== "https://disciplio.local/problems/ai-quota-exceeded") {
      return null;
    }
    return {
      feature: String(this.body.feature ?? "AI"),
      period: String(this.body.period ?? "DAY"),
      limit: Number(this.body.limit ?? 0),
      used: Number(this.body.used ?? 0),
      remaining: Number(this.body.remaining ?? 0),
      resetsAt: String(this.body.resetsAt ?? ""),
    };
  }
}

export function hasApiSession() {
  return Boolean(accessToken);
}

export function saveAuthTokens(response: Pick<AuthSessionResponse, "accessToken">) {
  accessToken = response.accessToken;
  lastRefreshError = null;
}

export async function beginNewApiSession() {
  // Authentication requests must be evaluated before replacing an existing session. Kept as a
  // compatibility no-op for callers that used the old preflight cleanup hook.
}

/** Clears the previous browser session after authentication has succeeded, without revoking it. */
export async function replaceApiSession() {
  const previousAccessToken = accessToken;
  resetAnalytics();
  clearLocalAuthState();
  const subscription = await currentBrowserPushSubscription().catch(() => null);
  if (subscription && previousAccessToken) {
    try {
      await fetch(`${API_BASE_URL}/api/users/me/push-subscriptions/current`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${previousAccessToken}` },
        credentials: "include",
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
    } catch {
      // The local unsubscribe below still prevents this browser from receiving future pushes.
    }
  }
  await unsubscribeBrowserPush().catch(() => null);
}

export async function logoutApiSession() {
  const logoutAccessToken = accessToken;
  resetAnalytics();
  clearLocalAuthState();
  const subscription = await currentBrowserPushSubscription().catch(() => null);
  if (subscription && logoutAccessToken) {
    try {
      await fetch(`${API_BASE_URL}/api/users/me/push-subscriptions/current`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${logoutAccessToken}` },
        credentials: "include",
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
    } catch {
      // The local unsubscribe below still prevents this browser from receiving future pushes.
    }
  }
  await unsubscribeBrowserPush().catch(() => null);
  try {
    await fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Local cleanup still prevents this browser from using the old access token.
  }
}

export function clearAuthTokens() {
  clearLocalAuthState();
  void unsubscribeBrowserPush().catch(() => null);
}

function clearLocalAuthState() {
  accessToken = null;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  USER_STATE_KEYS.forEach((key) => localStorage.removeItem(key));
  clearCoachLastConversationId();
  sessionStorage.removeItem(BROWSER_STORAGE_KEYS.goalCreationDrafts);
  // The welcome draft is deliberately NOT cleared here: a pending registration must keep its
  // in-progress draft available for the email-verification flow. It is cleared only where
  // onboarding finished or was explicitly abandoned.
}

export function isUnauthorizedError(error: unknown): boolean {
  return error instanceof DisciplioApiError && error.status === 401;
}

export function isRateLimitError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 429
    && error.body?.type !== "https://disciplio.local/problems/ai-quota-exceeded"
    && error.body?.type !== "https://disciplio.local/problems/authentication-throttled";
}

export function isAuthenticationThrottledError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 429
    && error.body?.type === "https://disciplio.local/problems/authentication-throttled";
}

/**
 * True when the API could not answer at all: the request never reached the server (status 0, the
 * sentinel used for a rejected fetch) or the server failed on its own side (5xx). Rate limits and
 * 4xx credential problems are deliberately excluded — they have dedicated messages.
 */
export function isServerUnavailableError(error: unknown): boolean {
  if (!(error instanceof DisciplioApiError)) return true;
  return error.status === 0 || error.status >= 500;
}

export function getRetryAfterSeconds(error: unknown, fallback = 1): number {
  if (!(error instanceof DisciplioApiError) || error.retryAfter == null) return fallback;
  return Math.max(1, error.retryAfter);
}

export function getLastRefreshError(): DisciplioApiError | null {
  return lastRefreshError;
}

export function isCalendarHabitTimeScopeRequiredError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 409
    && error.body?.type === "https://disciplio.local/problems/calendar-habit-time-scope-required";
}

export function isRegistrationEmailExistsError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 409
    && error.body?.type === "https://disciplio.local/problems/registration-email-exists";
}

export function isPasswordPolicyError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 400
    && error.body?.type === "https://disciplio.local/problems/password-policy";
}

export type PasswordPolicyReason = "TOO_SHORT" | "TOO_LONG" | "REUSED";

export function passwordPolicyReason(error: unknown): PasswordPolicyReason | null {
  if (!isPasswordPolicyError(error)) return null;
  const reason = (error as DisciplioApiError).body?.reason;
  return reason === "TOO_SHORT" || reason === "TOO_LONG" || reason === "REUSED"
    ? reason
    : null;
}

export function isConflictError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 409
    && error.body?.type === "https://disciplio.local/problems/conflict";
}

export function isEmailVerificationRequiredError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 403
    && error.body?.type === "https://disciplio.local/problems/email-verification-required";
}

export function isGoogleAuthDisabledError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 400
    && error.body?.type === "https://disciplio.local/problems/google-auth-disabled";
}

export function isGoogleCredentialInvalidError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 401
    && error.body?.type === "https://disciplio.local/problems/google-credential-invalid";
}

export function isGoogleAccountNotFoundError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 404
    && error.body?.type === "https://disciplio.local/problems/google-account-not-found";
}

export function isGoogleLinkRequiredError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 409
    && error.body?.type === "https://disciplio.local/problems/google-link-required";
}

export function isGoogleIdentityOwnedByAnotherUserError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 409
    && error.body?.type === "https://disciplio.local/problems/google-identity-owned-by-another-user";
}

export function isGoogleEmailMismatchError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 400
    && error.body?.type === "https://disciplio.local/problems/google-email-mismatch";
}

export function isOnlyAuthenticationMethodError(error: unknown): boolean {
  return error instanceof DisciplioApiError
    && error.status === 409
    && error.body?.type === "https://disciplio.local/problems/only-authentication-method";
}

function accessTokenExpiresAt(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
    const parsed = JSON.parse(decoded) as { exp?: unknown };
    return typeof parsed.exp === "number" ? parsed.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function ensureUsableAccessToken(): Promise<boolean> {
  if (!accessToken) return refreshAccessToken();

  const expiresAt = accessTokenExpiresAt(accessToken);
  if (expiresAt == null || expiresAt > Date.now() + ACCESS_TOKEN_REFRESH_SKEW_MS) return true;
  if (await refreshAccessToken()) return true;

  if (lastRefreshError?.status === 400 || lastRefreshError?.status === 401) {
    clearAuthTokens();
  }
  return false;
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const inFlight = coordinateRefresh();
  refreshInFlight = inFlight;
  void inFlight.then(
    () => { if (refreshInFlight === inFlight) refreshInFlight = null; },
    () => { if (refreshInFlight === inFlight) refreshInFlight = null; },
  );
  return inFlight;
}

async function coordinateRefresh(): Promise<boolean> {
  const startedAt = Date.now();
  const previousOutcomeIssuedAt = latestRefreshOutcome?.issuedAt ?? 0;
  const channel = getRefreshChannel();
  try {
    const outcome = await withRefreshLock(async () => {
      // A follower waits on the lock while the leader performs the request. The leader broadcasts
      // before releasing the lock, so the follower can apply that result and never hits the API.
      await Promise.resolve();
      const received = latestRefreshOutcome;
      if (received && received.issuedAt >= startedAt && received.issuedAt > previousOutcomeIssuedAt) {
        return received.outcome;
      }
      const fresh = await performRefreshRequest();
      if (channel) {
        const issuedAt = Date.now();
        latestRefreshOutcome = { issuedAt, outcome: fresh };
        try {
          channel.postMessage({ type: "refresh-outcome", issuedAt, outcome: fresh });
        } catch {
          // BroadcastChannel is best-effort; same-tab callers still share refreshInFlight.
        }
      }
      return fresh;
    });
    return applyRefreshOutcome(outcome);
  } catch {
    const outcome: RefreshOutcome = {
      ok: false,
      terminal: false,
      failure: { status: 0, message: "Unable to refresh session", body: null, retryAfter: null },
    };
    lastRefreshError = new DisciplioApiError(0, outcome.failure?.message);
    return false;
  }
}

async function performRefreshRequest(): Promise<RefreshOutcome> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    let body: Record<string, unknown> | null = null;
    if (!response.ok) {
      try {
        const parsed = await response.json();
        if (parsed && typeof parsed === "object") body = parsed as Record<string, unknown>;
      } catch {
        // Some infrastructure errors have no JSON body.
      }
      const retryAfter = parseRetryAfter(response.headers?.get?.("Retry-After"));
      const error = new DisciplioApiError(
        response.status,
        typeof body?.detail === "string" ? body.detail : undefined,
        body,
        retryAfter,
      );
      lastRefreshError = error;
      const terminal = response.status === 400 || response.status === 401;
      if (terminal) clearAuthTokens();
      return {
        ok: false,
        terminal,
        failure: { status: error.status, message: error.message, body: error.body, retryAfter: error.retryAfter },
      };
    }
    const payload = await response.json() as AuthSessionResponse;
    saveAuthTokens(payload);
    return { ok: true, terminal: false, accessToken: payload.accessToken };
  } catch {
    const error = new DisciplioApiError(0, "Unable to refresh session");
    lastRefreshError = error;
    return {
      ok: false,
      terminal: false,
      failure: { status: error.status, message: error.message, body: null, retryAfter: null },
    };
  }
}

function applyRefreshOutcome(outcome: RefreshOutcome): boolean {
  if (outcome.ok) {
    if (outcome.accessToken) saveAuthTokens({ accessToken: outcome.accessToken });
    return true;
  }
  const failure = outcome.failure;
  lastRefreshError = new DisciplioApiError(
    failure?.status ?? 0,
    failure?.message,
    failure?.body ?? null,
    failure?.retryAfter ?? null,
  );
  if (outcome.terminal) clearAuthTokens();
  return false;
}

function getRefreshChannel(): BroadcastChannel | null {
  if (refreshChannelInitialized) return refreshChannel;
  refreshChannelInitialized = true;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    refreshChannel = new BroadcastChannel(REFRESH_CHANNEL_NAME);
    refreshChannel.onmessage = (event: MessageEvent) => {
      const message = event.data as { type?: unknown; issuedAt?: unknown; outcome?: unknown } | null;
      if (message?.type !== "refresh-outcome" || typeof message.issuedAt !== "number" || !message.outcome) return;
      latestRefreshOutcome = { issuedAt: message.issuedAt, outcome: message.outcome as RefreshOutcome };
    };
  } catch {
    refreshChannel = null;
  }
  return refreshChannel;
}

function parseRetryAfter(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) return Math.ceil(value);
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) return Math.ceil(parsed);
  }
  return null;
}

function withRefreshLock<T>(operation: () => Promise<T>): Promise<T> {
  if (typeof navigator === "undefined" || !navigator.locks) return operation();
  return navigator.locks.request(
    REFRESH_LOCK_NAME,
    { mode: "exclusive" },
    operation
  );
}

export function restoreApiSession(): Promise<boolean> {
  return refreshAccessToken();
}

export async function requestResponse(path: string, options: RequestOptions & { _retried?: boolean } = {}): Promise<Response> {
  if (options.auth && !options._retried && !(await ensureUsableAccessToken())) {
    throw new DisciplioApiError(401, "Authentication required");
  }
  const headers = new Headers(options.headers);
  if (typeof FormData !== "undefined" && options.body instanceof FormData) {
    headers.delete("Content-Type");
  } else if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (options.auth) {
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  }
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
      credentials: "include",
    });
  } catch {
    // A rejected fetch means the request never reached the API (offline, DNS, TLS, aborted).
    // Status 0 is the shared "could not reach the server" sentinel, as in performRefreshRequest.
    throw new DisciplioApiError(0, "Unable to reach the Disciplio API");
  }
  // Correlates any Sentry event captured for this request with the same id Grafana/Langfuse/backend
  // logs use (see docs/OBSERVABILITY.md#correlation). Requires CORS exposedHeaders on the API side.
  setRequestIdTag(response.headers?.get?.(REQUEST_ID_HEADER));
  if (!response.ok) {
    if (options.auth && response.status === 401 && !options._retried) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return requestResponse(path, { ...options, _retried: true });
      }
    }
    let body: Record<string, unknown> | null = null;
    try {
      const parsed = await response.json();
      if (parsed && typeof parsed === "object") body = parsed as Record<string, unknown>;
    } catch {
      // Some infrastructure errors have no JSON body.
    }
    const retryAfter = parseRetryAfter(response.headers?.get?.("Retry-After"));
    throw new DisciplioApiError(response.status, typeof body?.detail === "string" ? body.detail : undefined, body, retryAfter);
  }
  return response;
}

export async function request<T = unknown>(path: string, options: RequestOptions & { _retried?: boolean } = {}): Promise<T> {
  const response = await requestResponse(path, options);
  if (response.status === 204) {
    return undefined as T;
  }
  // DELETE/POST handlers may legitimately acknowledge with another 2xx status and an empty
  // body. Treat that the same as 204 so a committed mutation is not reported as a frontend error.
  if (typeof response.text === "function") {
    const body = await response.text();
    if (!body.trim()) return undefined as T;
    return JSON.parse(body) as T;
  }
  return response.json() as Promise<T>;
}
